import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LuChevronRight,
  LuClipboardList,
  LuGraduationCap,
} from "@platform/icon";
import type {
  EducationCourse,
  EducationDeliveryMode,
  EducationSession,
} from "@platform/billing";
import { PATHS } from "@/shared/constants/app/routes.constant.ts";
import {
  useEducationCourses,
  useEducationSessions,
} from "../api/use-education.query";
import { useEducationCart } from "../model/education-cart.context";
import { CourseCard } from "../components/CourseCard";
import { SessionCard } from "../components/SessionCard";
import { CartBar } from "../components/CartBar";
import { EducationProgressSteps } from "../components/EducationProgressSteps";
import { EDUCATION_STEP } from "../constants/education-step.constant";

type ModeFilter = "ALL" | EducationDeliveryMode;

const MODE_TABS: { value: ModeFilter; label: string }[] = [
  { value: "ALL", label: "전체" },
  { value: "ONLINE", label: "온라인" },
  { value: "OFFLINE", label: "오프라인" },
];

/**
 * 선택한 과정을 담는 쿼리 파라미터. 단계를 로컬 state 로만 두면 회차 화면에서 뒤로가기가
 * 과정 목록이 아니라 교육 밖으로 나가고, 새로고침하면 1단계로 튕긴다.
 */
const COURSE_PARAM = "course";

const SESSION_PAGE_SIZE = 50;

function CourseStep({
  courses,
  loading,
  onSelect,
}: {
  courses: EducationCourse[];
  loading: boolean;
  onSelect: (course: EducationCourse) => void;
}) {
  return (
    <section aria-labelledby="courses-heading">
      <div className="mb-4">
        <h2
          id="courses-heading"
          className="text-xl font-bold text-gray-950 dark:text-neutral-100"
        >
          교육과정을 선택해 주세요
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
          교육 대상과 내용을 비교한 뒤 회차를 확인하세요.
        </p>
      </div>

      {loading ? (
        <p className="rounded-lg border border-gray-200 py-12 text-center text-sm text-gray-500 dark:border-white/10 dark:text-neutral-400">
          과정을 불러오는 중…
        </p>
      ) : courses.length === 0 ? (
        <p className="rounded-lg border border-gray-200 py-12 text-center text-sm text-gray-500 dark:border-white/10 dark:text-neutral-400">
          공개된 교육 과정이 없습니다.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} onSelect={onSelect} />
          ))}
        </div>
      )}
    </section>
  );
}

function SessionStep({
  course,
  sessions,
  loading,
  mode,
  onModeChange,
  onBack,
  isSelected,
  onToggle,
}: {
  course?: EducationCourse;
  sessions: EducationSession[];
  loading: boolean;
  mode: ModeFilter;
  onModeChange: (mode: ModeFilter) => void;
  onBack: () => void;
  isSelected: (id: string) => boolean;
  onToggle: (session: EducationSession) => void;
}) {
  return (
    <section aria-labelledby="open-sessions-heading">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 inline-flex items-center text-xs font-semibold text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-neutral-400 dark:hover:text-white"
          >
            <span aria-hidden className="mr-1">
              ←
            </span>
            교육과정 다시 선택
          </button>
          <div className="flex items-center gap-2">
            <h2
              id="open-sessions-heading"
              className="text-xl font-bold text-gray-950 dark:text-neutral-100"
            >
              접수 중인 회차
            </h2>
            {!loading && (
              <span className="text-sm font-semibold tabular-nums text-brand-700 dark:text-brand-300">
                {sessions.length}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">
            {course ? `${course.title} · ` : ""}여러 회차를 한 번에 선택할 수
            있습니다.
          </p>
        </div>
        <div
          className="flex w-fit rounded-lg bg-gray-100 p-1 dark:bg-white/10"
          role="group"
          aria-label="교육 진행 방식"
        >
          {MODE_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => onModeChange(tab.value)}
              aria-pressed={mode === tab.value}
              className={`rounded-md px-3.5 py-2 text-sm font-medium ${
                mode === tab.value
                  ? "bg-white text-gray-950 shadow-sm dark:bg-neutral-700 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="rounded-lg border border-gray-200 py-14 text-center text-sm text-gray-500 dark:border-white/10 dark:text-neutral-400">
          회차를 불러오는 중…
        </p>
      ) : sessions.length === 0 ? (
        <div className="rounded-lg border border-gray-200 py-14 text-center dark:border-white/10">
          <LuGraduationCap
            aria-hidden
            className="mx-auto h-6 w-6 text-gray-300 dark:text-neutral-600"
          />
          <p className="mt-3 text-sm text-gray-600 dark:text-neutral-300">
            현재 접수 중인 회차가 없습니다.
          </p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-gray-200 dark:border-white/15">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              brandName={course?.family}
              selected={isSelected(session.id)}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** 교육과정을 고르면 그 과정의 회차 화면으로 넘어가는 단계형 교육 접수. */
export function EducationListView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<ModeFilter>("ALL");
  const selectedCourseId = searchParams.get(COURSE_PARAM);

  const { data: courses = [], isLoading: coursesLoading } =
    useEducationCourses();
  const selectedCourse = courses.find(
    (course) => course.id === selectedCourseId,
  );
  const activeStep = selectedCourse
    ? EDUCATION_STEP.SESSION
    : EDUCATION_STEP.COURSE;
  const { data: sessions, isLoading: sessionsLoading } = useEducationSessions({
    applicableOnly: true,
    // 마감 회차도 일정 확인용으로 보여주고, 담기는 SessionCard 가 막는다.
    includeSoldOut: true,
    ...(selectedCourse ? { courseId: selectedCourse.id } : {}),
    ...(mode === "ALL" ? {} : { deliveryMode: mode }),
    limit: SESSION_PAGE_SIZE,
  });
  const cart = useEducationCart();
  const visibleSessions = selectedCourse ? (sessions?.items ?? []) : [];

  const selectCourse = (course: EducationCourse) =>
    setSearchParams({ [COURSE_PARAM]: course.id });
  const showCourses = () => setSearchParams({});

  const toggle = (session: EducationSession) =>
    cart.toggle({
      sessionId: session.id,
      courseId: session.courseId,
      courseTitle: session.courseTitle,
      sessionTitle: session.title,
      startAt: session.startAt,
      deliveryMode: session.deliveryMode,
      price: session.price,
    });

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
      <header className="grid gap-6 border-b border-gray-200 pb-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-end dark:border-white/10">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-950 dark:text-white">
            적산 교육
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600 dark:text-neutral-300">
            실제 업무 흐름을 익히는 적산 실무 교육입니다. 원하는 과정과 일정을
            선택해 접수할 수 있습니다.
          </p>
        </div>
        <Link
          to={PATHS.EDUCATION_REGISTRATIONS}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-white/20 dark:bg-transparent dark:text-neutral-100 dark:hover:bg-white/10"
        >
          <LuClipboardList aria-hidden className="h-4 w-4" />
          내 접수 내역
          <LuChevronRight aria-hidden className="h-4 w-4 text-gray-400" />
        </Link>
      </header>

      <div className="mt-6">
        <EducationProgressSteps
          activeStep={activeStep}
          maxSelectableStep={activeStep}
          onStepSelect={(step) => {
            if (step === EDUCATION_STEP.COURSE) showCourses();
          }}
        />
      </div>

      <div className="pt-10">
        {/* key 를 단계로 두면 화면이 갈릴 때만 전환이 돈다 — 목록 내부 갱신에는 반응하지 않는다. */}
        <div key={activeStep} className="education-pane-enter">
          {activeStep === 1 ? (
            <CourseStep
              courses={courses}
              loading={coursesLoading}
              onSelect={selectCourse}
            />
          ) : (
            <SessionStep
              course={selectedCourse}
              sessions={visibleSessions}
              loading={sessionsLoading}
              mode={mode}
              onModeChange={setMode}
              onBack={showCourses}
              isSelected={cart.has}
              onToggle={toggle}
            />
          )}
        </div>
      </div>

      <CartBar />
    </main>
  );
}
